import { PoStorageService } from '@po-ui/ng-storage';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { UserAccessRules } from '../../interfaces/user-access-rules.model';
import { MenuAccessRules, UserAccessRulesEnum } from './../../interfaces/user-access-rules.model';

const URL = 'api/tgv/userAccessRules/';
@Injectable({
  providedIn: 'root'
})
export class UserAccessRulesService {
  constructor(
    private httpClient: HttpClient,
    private poStorageService: PoStorageService
  ) {}

  getUserAccessRules(): Observable<UserAccessRules> {
    return this.httpClient.get<UserAccessRules>(URL).pipe(take(1));
  }

  setUserAccessRulesOnStorage(rules: UserAccessRules): void {
    localStorage.setItem('userAccessRules', JSON.stringify(rules));
  }

  getUserAccessRulesOnStorage(): string {
    return localStorage.getItem('userAccessRules');
  }

  isUserAccessAllowed(url: string): boolean {
    const rulesMenu = JSON.parse(this.getUserAccessRulesOnStorage()).items;
    const treatedMenuName =
      url.includes('sales') && !url.includes('/sales-goals') ? this.handleMenuName('sales') : this.handleMenuName(url);
    const isMenuAllowed = this.isMenuAccessAllowed(url, rulesMenu, treatedMenuName);
    if (isMenuAllowed) {
      const rulesSubMenu = rulesMenu.filter(i => i.menu === treatedMenuName && i.canAccessMenu)[0];
      if (rulesSubMenu) {
        return this.isSubMenuAccessAllowed(treatedMenuName, url, rulesSubMenu);
      }
    }
    return isMenuAllowed;
  }

  isMenuAccessAllowed(menuName: string, rules: Array<MenuAccessRules>, treatedMenuName: string): boolean {
    if (menuName === treatedMenuName) {
      return true;
    } else {
      const rule: Array<MenuAccessRules> = rules.filter(rule => rule.menu === treatedMenuName && rule.canAccessMenu);
      return rule.length > 0;
    }
  }

  isSubMenuAccessAllowed(menuName: string, url: string, rulesMenu: MenuAccessRules): boolean {
    const subMenuName = this.handleMenuName(url);
    const rulesSubMenu =
      menuName === subMenuName ? rulesMenu : rulesMenu.subMenu.filter(i => i.menu === subMenuName)[0];
    return this.isOperationAccessAllowed(rulesSubMenu, url);
  }

  isOperationAccessAllowed(rulesSubMenu: MenuAccessRules, url: string): boolean {
    const actions = rulesSubMenu.actions;
    const urlLength = url.split('/').length;
    const minLength = url.includes('/sales-order/') ? 5 : 6;

    const urlPermissions = {
      '/details/': actions?.canVisualize,
      '/maintenance/': urlLength > minLength ? actions?.canUpdate : actions?.canInclude,
      'customers/edit/': actions?.canUpdate,
      'customers/new': actions?.canInclude,
      'prospects/edit/': actions?.canUpdate,
      'prospects/new': actions?.canInclude,
      'contacts/edit/': actions?.canUpdate,
      'contacts/new': actions?.canInclude
    };

    for (const pattern in urlPermissions) {
      if (url.includes(pattern)) {
        return urlPermissions[pattern];
      }
    }

    return rulesSubMenu.canAccessMenu;
  }

  handleMenuName(route: string): string {
    if (route === '/notifications') {
      return UserAccessRulesEnum.notifications;
    } else if (route.includes('/customers')) {
      return UserAccessRulesEnum.customers;
    } else if (route.includes('/prospects')) {
      return UserAccessRulesEnum.prospects;
    } else if (route.includes('/contacts')) {
      return UserAccessRulesEnum.contacts;
    } else if (route.includes('/sales-goals')) {
      return UserAccessRulesEnum.salesGoals;
    } else if (route.includes('/sales-order')) {
      return UserAccessRulesEnum.salesOrders;
    } else if (route.includes('/sales-budget')) {
      return UserAccessRulesEnum.salesBudgets;
    } else if (route.includes('/products')) {
      return UserAccessRulesEnum.products;
    } else if (route.includes('/commissions')) {
      return UserAccessRulesEnum.commissions;
    } else if (route === 'sales') {
      return UserAccessRulesEnum.sales;
    } else {
      return route;
    }
  }

  canAccessMenuVersion(item: string): boolean {
    const serverVersion = localStorage.getItem('serverVersion');

    switch (item) {
      case UserAccessRulesEnum.contacts:
        return this.compareVersions(serverVersion, '1.8.0') >= 0;
      case UserAccessRulesEnum.salesGoals:
      case '/sales-goals':
        return this.canAccessAlias([]) && this.compareVersions(serverVersion, '1.8.1') >= 0;
      case '/pdf-custom':
        return this.compareVersions(serverVersion, '1.11.3') >= 0;
      case '/pdf-default-logo':
        return this.compareVersions(serverVersion, '1.11.6') >= 0;
      default:
        return true;
    }
  }

  canAccessAlias(aliasList:Array<string>): boolean {
    const allowedAliasList = aliasList;
    const alias = allowedAliasList.length > 0 ? localStorage.getItem('ALIAS').toUpperCase(): undefined;
    return alias ? allowedAliasList.includes(alias): true;
  }

  countryAliasInvoices(): Promise<string> {
    return this.poStorageService.get('MV_PAISLOC');
  }

  compareVersions(v1: string, v2: string): number {
    const v1Parts = v1.split('.').map(Number);
    const v2Parts = v2.split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const num1 = v1Parts[i] || 0;
      const num2 = v2Parts[i] || 0;

      if (num1 < num2) return -1;
      if (num1 > num2) return 1;
    }

    return 0;
  }
}
